import {
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useState } from "react";
import { FaColumns, FaEdit, FaInbox, FaSort, FaSortDown, FaSortUp, FaTrash } from "react-icons/fa";
import { Lead } from "../../../models/Lead";
import { leadColumns } from "./leadColumns";
import { crm } from "./crmTheme";

type SortDir = "asc" | "desc";

const LeadTable = ({
  leads,
  selectedColumns,
  onEdit,
  onDelete,
  onOpenColumnsModal,
}: {
  leads: Lead[];
  selectedColumns: string[];
  onEdit: (lead: Lead) => void;
  onDelete: (lead: Lead) => void;
  onOpenColumnsModal: () => void;
}) => {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const visibleColumns = leadColumns.filter((c) => selectedColumns.includes(c.label));

  const sorted = [...leads].sort((a, b) => {
    if (!sortField) return 0;
    const col = leadColumns.find((c) => c.index === sortField);
    if (!col) return 0;
    const va = String(col.data(a) ?? "");
    const vb = String(col.data(b) ?? "");
    return sortDir === "asc" ? va.localeCompare(vb, "pt-BR") : vb.localeCompare(va, "pt-BR");
  });

  const handleSort = (index: string) => {
    if (sortField === index) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(index);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <FaSort color={crm.textFaint} />;
    return sortDir === "asc"
      ? <FaSortUp color={crm.brand} />
      : <FaSortDown color={crm.brand} />;
  };

  return (
    <Box>
      <Flex justify="flex-end" mb="12px">
        <Button
          leftIcon={<FaColumns />}
          variant="outline"
          size="sm"
          borderColor={crm.border}
          color={crm.text}
          _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
          borderRadius="10px"
          onClick={onOpenColumnsModal}
        >
          Personalizar colunas
        </Button>
      </Flex>

      {/* Card wrapper */}
      <Box
        bg={crm.surface}
        border={`1px solid ${crm.border}`}
        borderRadius="14px"
        boxShadow={crm.shadowSm}
        overflow="hidden"
      >
        <TableContainer overflowX="auto" overflowY="auto" maxH="calc(100vh - 310px)">
          <Table variant="simple" fontFamily="Inter" size="sm">
            <Thead>
              <Tr>
                {visibleColumns.map((col) => (
                  <Th
                    key={col.label}
                    cursor="pointer"
                    onClick={() => handleSort(col.index)}
                    position="sticky"
                    top={0}
                    bg={crm.subtle}
                    zIndex={1}
                    textTransform="none"
                    color={crm.textMuted}
                    fontSize="xs"
                    fontWeight="700"
                    py="12px"
                    borderColor={crm.border}
                  >
                    <Flex align="center" gap="6px">
                      {col.label}
                      <Box>
                        <SortIcon field={col.index} />
                      </Box>
                    </Flex>
                  </Th>
                ))}
                <Th
                  position="sticky"
                  top={0}
                  right={0}
                  bg={crm.subtle}
                  zIndex={2}
                  textTransform="none"
                  w="72px"
                  borderColor={crm.border}
                />
              </Tr>
            </Thead>
            <Tbody>
              {sorted.length === 0 ? (
                <Tr>
                  <Td colSpan={visibleColumns.length + 1}>
                    <Flex
                      flexDir="column"
                      align="center"
                      justify="center"
                      py="48px"
                      gap="10px"
                      color={crm.textFaint}
                    >
                      <FaInbox size={28} />
                      <Text fontSize="sm">Nenhum lead encontrado.</Text>
                      <Text fontSize="xs" color={crm.textFaint}>
                        Clique em "Novo lead" para adicionar.
                      </Text>
                    </Flex>
                  </Td>
                </Tr>
              ) : (
                sorted.map((lead) => (
                  <Tr
                    key={lead.id}
                    cursor="pointer"
                    _hover={{ bg: crm.brandSoft }}
                    onClick={() => onEdit(lead)}
                    borderColor={crm.border}
                  >
                    {visibleColumns.map((col) => (
                      <Td
                        key={col.label}
                        borderColor={crm.border}
                        fontSize="sm"
                        color={crm.text}
                        py="12px"
                      >
                        {col.data(lead)}
                      </Td>
                    ))}
                    <Td
                      position="sticky"
                      right={0}
                      bg={crm.surface}
                      _groupHover={{ bg: crm.brandSoft }}
                      onClick={(e) => e.stopPropagation()}
                      borderColor={crm.border}
                    >
                      <Flex gap="4px">
                        <IconButton
                          aria-label="Editar lead"
                          icon={<FaEdit />}
                          size="xs"
                          variant="ghost"
                          color={crm.textMuted}
                          _hover={{ color: crm.brand, bg: crm.brandSoft }}
                          onClick={() => onEdit(lead)}
                        />
                        <IconButton
                          aria-label="Excluir lead"
                          icon={<FaTrash />}
                          size="xs"
                          variant="ghost"
                          color={crm.textMuted}
                          _hover={{ color: crm.danger, bg: "#fef2f2" }}
                          onClick={() => onDelete(lead)}
                        />
                      </Flex>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default LeadTable;
